/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { Component, Input, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";
import { ChipModule } from "primeng/chip";
import { CardModule } from "primeng/card";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { TooltipModule } from "primeng/tooltip";
import { AutoCompleteModule } from "primeng/autocomplete";
import type {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from "primeng/autocomplete";
import { ConfirmationService, MessageService } from "primeng/api";
import { Subject, takeUntil } from "rxjs";
import { ScheduledOrder } from "../../../../../core/models/scheduled-order.interface";
import { ScheduledOrderCoupon } from "../../../../../core/models/scheduled-order-coupon.interface";
import { ScheduledOrderService } from "../../../services/scheduled-order.service";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../../core/services/qpilot.service";
import { SessionService } from "../../../../../core/services/session.service";
import { ScheduledOrderForm } from "../../../interfaces/scheduled-order-form.interface";

/**
 * Coupon interface matching old QMC QPilotCoupon
 */
interface Coupon {
  Id: number;
  Code: string;
  Name: string;
  Amount: number;
  DiscountType: string;
  MaxPercentageDiscount?: number;
  Active: boolean;
  Valid: boolean;
}

/**
 * Coupon manager component for adding, validating, and managing coupons
 * Handles both the Coupons string (comma-separated codes) and CouponsHistory array
 */
@Component({
  selector: "app-coupon-manager",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    ChipModule,
    CardModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    TooltipModule,
    AutoCompleteModule,
  ],
  providers: [ConfirmationService],
  templateUrl: "./coupon-manager.component.html",
  styleUrl: "./coupon-manager.component.scss",
})
export class CouponManagerComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() formGroup!: FormGroup<ScheduledOrderForm>;
  @Input() siteId!: number;
  @Input() mode: "create" | "edit" = "create"; // Add mode input to determine edit vs create
  @Input() scheduledOrder?: ScheduledOrder; // Add scheduledOrder input for edit mode context
  @Input() disabled = false;
  @Input() accessToken?: QPilotAccessToken; // Optional token for embedded mode

  // FormControl for coupon input
  couponInputControl = new FormControl<string>("");

  // Component state
  isValidating = false;
  validationAttempted = false;

  // Autocomplete state
  filteredCoupons: Coupon[] = [];
  searchTimeout: any = null;

  constructor(
    private scheduledOrderService: ScheduledOrderService,
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  /**
   * Build ScheduledOrder from form values for API calls
   * Used when validating coupons or calling totals API
   */
  private buildOrderFromForm(): ScheduledOrder {
    const formValues = this.formGroup.value;

    // Use the mode input to determine if this is CREATE or EDIT mode
    // CRITICAL: Must match the actual mode to send proper fields to API
    const isEditMode = this.mode === "edit";
    console.log("[COUPON-MANAGER DEBUG] buildOrderFromForm called", {
      mode: this.mode,
      isEditMode,
      hasScheduledOrder: !!this.scheduledOrder,
      scheduledOrderId: this.scheduledOrder?.Id,
      formCoupons: formValues.coupons,
      formCouponsHistory: formValues.couponsHistory,
    });

    // Map form items (lowercase) to API ScheduledOrderItems (PascalCase)
    // CRITICAL: Only include enabled items and proper fields based on mode
    const scheduledOrderItems =
      formValues.scheduledOrderItems
        ?.filter((item) => {
          // Only include items that have a product and aren't disabled
          return item.product && !item.disabled;
        })
        .map((item) => {
          // Base fields for both CREATE and EDIT
          const baseItem: any = {
            Quantity: item.quantity || 1,
            Product: item.product,
            ProductId: item.productId || 0,
            Price: item.price || 0,
            SalePrice: item.salePrice,
          };

          // Add EDIT-only fields if in edit mode and item has an ID
          if (isEditMode && item.id) {
            baseItem.Id = item.id;
            baseItem.ScheduledOrderId = item.scheduledOrderId || 0;
            baseItem.OriginalSalePrice =
              item.originalSalePrice ?? item.salePrice;
            baseItem.Cycles = item.cycles ?? 0;
            baseItem.MinCycles = item.minCycles ?? null;
            baseItem.MaxCycles = item.maxCycles ?? null;
            baseItem.CreatedUtc = item.createdUtc || undefined;
            baseItem.UpdatedUtc = item.updatedUtc || undefined;
            baseItem.Metadata = item.metadata || null;
          } else {
            // CREATE mode: always include Cycles (default to 0 if not set, matching prepareOrderForSave)
            // The API expects Cycles to be present (0 = infinite cycles in QPilot)
            baseItem.Cycles = item.cycles ?? 0;
            if (item.minCycles !== undefined)
              baseItem.MinCycles = item.minCycles;
            if (item.maxCycles !== undefined)
              baseItem.MaxCycles = item.maxCycles;
          }

          return baseItem;
        }) || [];

    return {
      Id: isEditMode ? this.scheduledOrder?.Id || 0 : 0,
      CustomerId: formValues.customerId!,
      Customer: formValues.customer!,
      ScheduledOrderItems: scheduledOrderItems as any,
      Coupons: formValues.coupons || "",
      CouponsHistory: formValues.couponsHistory || [],
      FrequencyType: formValues.frequencyType!,
      Frequency: formValues.frequency!,
      Cycles: formValues.cycles!,
      NextOccurrenceUtc: formValues.nextOccurrenceUtc as any,
      Status: formValues.status!,
      ShippingFirstName: formValues.shippingFirstName!,
      ShippingLastName: formValues.shippingLastName!,
      ShippingStreet1: formValues.shippingStreet1!,
      ShippingStreet2: formValues.shippingStreet2!,
      ShippingCity: formValues.shippingCity!,
      ShippingState: formValues.shippingState!,
      ShippingPostcode: formValues.shippingPostcode!,
      ShippingCountry: formValues.shippingCountry!,
      PreferredShippingRateOption:
        formValues.preferredShippingRateOption || undefined,
      EnableAutoShippingRate: formValues.enableAutoShippingRate!,
      PaymentMethodId: formValues.paymentMethodId || undefined,
      PaymentMethod: formValues.paymentMethod || undefined,
      Name: formValues.name!,
      Note: formValues.note!,
      NextOccurrenceOffset: formValues.nextOccurrenceOffset!,
      CurrencyIso: formValues.currencyIso!,
      UtcOffset: formValues.utcOffset!,
      DisplaySalePrice: formValues.displaySalePrice!,
      FallbackToQpilotShippingWhenNextimeFails: false,
      FreeShipping: {
        OptionValue: "false",
        OptionText: "Free Shipping Disabled",
        IsDefault: true,
      },
      ProcessingErrorCode: 0,
      ShippingRateOptions: [],
      Metadata: null,
      ValidationResult: undefined,
      TotalNumberOfProcessingCycles: undefined,
      PreValidateSynchronously: false,
      Locked: false,
      LastEditableDate: new Date(),
      IsInDunning: false,
    } as unknown as ScheduledOrder;
  }

  /**
   * Handle focus event on autocomplete to fetch all coupons
   * Triggered when user clicks on the empty field
   */
  onCouponFocus(): void {
    // If field is empty and no coupons loaded yet, fetch all coupons
    const inputValue = this.couponInputControl.value || "";
    if (!inputValue && this.filteredCoupons.length === 0) {
      this.searchCoupons({ query: "" } as AutoCompleteCompleteEvent);
    }
  }

  /**
   * Search coupons with debouncing (like old QMC)
   * Triggered by autocomplete input or dropdown click
   * When query is empty, fetches all coupons from page 1
   * When typing, requires at least 2 characters for search
   */
  searchCoupons(event: AutoCompleteCompleteEvent): void {
    const query = event.query || "";

    // When query is empty (dropdown click), fetch all coupons from page 1
    // When typing, require at least 2 characters for search
    if (query.length > 0 && query.length < 2) {
      this.filteredCoupons = [];
      return;
    }

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce search by 300ms (matching old QMC)
    // NOTE: We search even with empty query to show all coupons when dropdown is clicked
    this.searchTimeout = setTimeout(() => {
      const accessToken = this.accessToken ?? this.sessionService.accessToken;

      if (!accessToken) {
        this.messageService.add({
          severity: "error",
          summary: "Authentication Error",
          detail: "Please sign in again.",
        });
        return;
      }

      // Call API with query (empty string will fetch all coupons from page 1)
      this.qpilotService
        .getCouponsForSite(
          this.siteId,
          accessToken,
          1,
          query.trim() || undefined,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.filteredCoupons = result.Items || [];
          },
          error: (err) => {
            console.error("Error searching coupons:", err);
            this.filteredCoupons = [];
            this.messageService.add({
              severity: "warn",
              summary: "Search Failed",
              detail: "Unable to search coupons. Please try again.",
              life: 3000,
            });
          },
        });
    }, 300);
  }

  /**
   * Format coupon for display in autocomplete dropdown (like old QMC)
   * Format: "CODE (Discount Description)"
   * Examples: "SAVE10 (Subtotal by 10% Off)", "SHIP25 (Shipping by $25 Off)"
   */
  formatCoupon(coupon: Coupon): string {
    if (!coupon) {
      return "";
    }

    let discountDescription = "";

    switch (coupon.DiscountType) {
      case "ReduceSubtotalByPercentage":
        discountDescription = `Subtotal by ${coupon.Amount}% Off`;
        if (coupon.MaxPercentageDiscount) {
          discountDescription += ` up to $${coupon.MaxPercentageDiscount}`;
        }
        break;
      case "ReduceSubtotalByAmount":
        discountDescription = `Subtotal by $${coupon.Amount} Off`;
        break;
      case "ReduceShippingByPercentage":
        discountDescription = `Shipping by ${coupon.Amount}% Off`;
        if (coupon.MaxPercentageDiscount) {
          discountDescription += ` up to $${coupon.MaxPercentageDiscount}`;
        }
        break;
      case "ReduceShippingByAmount":
        discountDescription = `Shipping by $${coupon.Amount} Off`;
        break;
      case "SetShippingToAmount":
        discountDescription = `Shipping set to $${coupon.Amount}`;
        break;
      default:
        discountDescription = coupon.Name || "Discount";
        break;
    }

    return `${coupon.Code} (${discountDescription})`;
  }

  /**
   * Handle coupon selection from autocomplete (like old QMC)
   * Adds coupon to CouponsHistory and validates immediately (matching old QMC behavior)
   *
   * TODO: Complete refactor to use form controls:
   * - Replace `this.order.CouponsHistory.some()` with `this.formGroup.controls.couponsHistory.value.some()`
   * - Replace `this.order.Id` with form value or 0 for create mode
   * - Replace `this.order.CouponsHistory.push()` with form control update
   * - Replace `this.order.Coupons = X` with `this.formGroup.controls.coupons.setValue(X)`
   * - Replace `this.couponInput = ""` with `this.couponInputControl.setValue("")`
   * - Remove `emitChange()` - form handles sync
   */
  onCouponSelect(event: AutoCompleteSelectEvent): void {
    const coupon = event.value as Coupon;
    if (!coupon) {
      return;
    }

    const couponsHistory = this.formGroup.controls.couponsHistory.value;

    // Check if coupon already exists (by ID, like old QMC)
    if (
      couponsHistory.some((c: ScheduledOrderCoupon) => c.CouponId === coupon.Id)
    ) {
      this.messageService.add({
        severity: "info",
        summary: "Coupon Already Added",
        detail: `Coupon "${coupon.Code}" is already applied to this order.`,
        life: 3000,
      });
      // Clear input immediately after showing message
      setTimeout(() => {
        this.couponInputControl.setValue("");
      }, 0);
      return;
    }

    // Create ScheduledOrderCoupon object (like old QMC)
    const newCoupon: ScheduledOrderCoupon = {
      ScheduledOrderId: 0, // Will be set on server
      CouponId: coupon.Id,
      Coupon: coupon as any,
      Valid: true,
      CouponInvalidationCode: 0,
    };

    // Add to CouponsHistory
    const updatedHistory = [...couponsHistory, newCoupon];
    this.formGroup.controls.couponsHistory.setValue(updatedHistory);

    // Update Coupons string (comma-separated codes)
    const couponCodes = updatedHistory
      .map((c: ScheduledOrderCoupon) => c.Coupon?.Code)
      .filter((code: string | undefined) => code)
      .join(",");
    this.formGroup.controls.coupons.setValue(couponCodes);

    // Clear input after selection
    setTimeout(() => {
      this.couponInputControl.setValue("");
    }, 0);

    // Validate coupons via API (like old QMC refreshTotals)
    // Form automatically syncs - no need to emit
    this.validateCouponsAndRefresh();
  }

  /**
   * Add and validate entered coupon codes
   * Supports comma-separated codes like "SAVE10,SHIP25"
   * NOTE: This method is now secondary to autocomplete, but kept for manual entry
   */
  addCoupons(): void {
    const inputValue = this.couponInputControl.value || "";
    if (!inputValue.trim()) {
      return;
    }

    // Parse comma-separated codes and remove whitespace
    const codes = inputValue
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    if (codes.length === 0) {
      return;
    }

    // Merge with existing coupons (avoid duplicates)
    const existingCoupons = this.formGroup.controls.coupons.value || "";
    const existingCodes = existingCoupons
      ? existingCoupons.split(",").filter((c) => c.length > 0)
      : [];
    const newCodes = codes.filter((code) => !existingCodes.includes(code));

    if (newCodes.length === 0) {
      // All codes already exist
      this.couponInputControl.setValue("");
      return;
    }

    // Update Coupons string
    const allCodes = [...existingCodes, ...newCodes];
    this.formGroup.controls.coupons.setValue(allCodes.join(","));

    // Clear input
    this.couponInputControl.setValue("");

    // Validate via API
    this.validateCoupons();
  }

  /**
   * Validate all coupons via API
   * Updates CouponsHistory with validation results
   */
  validateCoupons(): void {
    const coupons = this.formGroup.controls.coupons.value || "";
    if (!coupons || coupons.trim() === "") {
      this.formGroup.controls.couponsHistory.setValue([]);
      return;
    }

    this.isValidating = true;
    this.validationAttempted = true;

    const order = this.buildOrderFromForm();

    this.scheduledOrderService
      .validateCoupons(this.siteId, order, this.accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (validatedCoupons) => {
          this.formGroup.controls.couponsHistory.setValue(validatedCoupons);
          this.isValidating = false;
        },
        error: () => {
          this.isValidating = false;
          // CouponsHistory stays as is or empty
        },
      });
  }

  /**
   * Validate coupons and trigger totals refresh
   * Matches old QMC refreshTotals() behavior:
   * 1. Validates coupons via API
   * 2. Updates CouponsHistory with validation results
   * 3. Form automatically syncs (triggers totals recalculation)
   */
  private validateCouponsAndRefresh(): void {
    const coupons = this.formGroup.controls.coupons.value || "";
    const customerId = this.formGroup.controls.customerId.value;
    const couponsHistory = this.formGroup.controls.couponsHistory.value;

    // If no coupons, just clear
    if (!coupons || coupons.trim() === "") {
      this.formGroup.controls.couponsHistory.setValue([]);
      return;
    }

    // Skip validation if no customer selected (like old QMC)
    if (!customerId) {
      return;
    }

    this.isValidating = true;

    const order = this.buildOrderFromForm();

    this.scheduledOrderService
      .validateCoupons(this.siteId, order, this.accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (validatedCoupons) => {
          // Update CouponsHistory with validated results (like old QMC)
          // Maps validation results back to existing coupons in CouponsHistory
          const updatedHistory = couponsHistory.map((it) => {
            const validated = validatedCoupons.find(
              (x) => x.CouponId === it.CouponId,
            );
            if (validated) {
              it.Valid = validated.Valid;
              if (validated.Coupon) {
                it.Coupon.Valid = validated.Coupon.Valid;
              }
              it.CouponInvalidationCode = validated.CouponInvalidationCode;
            }
            return it;
          });

          this.formGroup.controls.couponsHistory.setValue(updatedHistory);
          this.isValidating = false;

          // Show validation feedback if any coupons are invalid
          const invalidCount = updatedHistory.filter((c) => !c.Valid).length;
          if (invalidCount > 0) {
            this.messageService.add({
              severity: "warn",
              summary: "Coupon Validation",
              detail: `${invalidCount} coupon${invalidCount > 1 ? "s" : ""} ${invalidCount > 1 ? "are" : "is"} invalid. Hover over the coupon for details.`,
              life: 5000,
            });
          }
        },
        error: () => {
          this.isValidating = false;
        },
      });
  }

  /**
   * Remove a coupon from the order (like old QMC)
   * Validates remaining coupons after removal
   * @param coupon Coupon to remove
   */
  removeCoupon(coupon: ScheduledOrderCoupon): void {
    const couponCode = coupon.Coupon?.Code || "";

    this.confirmationService.confirm({
      message: `Remove coupon "${couponCode}"?`,
      header: "Confirm Removal",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        const couponsHistory = this.formGroup.controls.couponsHistory.value;
        const couponsString = this.formGroup.controls.coupons.value || "";

        // Remove from CouponsHistory
        const updatedHistory = couponsHistory.filter(
          (c) => c.CouponId !== coupon.CouponId,
        );
        this.formGroup.controls.couponsHistory.setValue(updatedHistory);

        // Remove from Coupons string
        const codes = couponsString
          .split(",")
          .filter((code) => code !== couponCode);
        this.formGroup.controls.coupons.setValue(codes.join(","));

        // Validate remaining coupons and refresh totals (like old QMC refreshTotals)
        this.validateCouponsAndRefresh();
      },
    });
  }

  /**
   * Clear all coupons
   */
  clearAllCoupons(): void {
    this.confirmationService.confirm({
      message: "Remove all coupons from this order?",
      header: "Confirm Clear All",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.formGroup.controls.coupons.setValue("");
        this.formGroup.controls.couponsHistory.setValue([]);
        this.couponInputControl.setValue("");
        this.validationAttempted = false;
      },
    });
  }

  /**
   * Get display label for a coupon
   * @param coupon Coupon object
   * @returns Display string with code and amount
   */
  getCouponLabel(coupon: ScheduledOrderCoupon): string {
    const code = coupon.Coupon?.Code || "Unknown";
    const amount = coupon.Coupon?.Amount;
    const type = coupon.Coupon?.DiscountType || "";

    if (amount !== undefined && amount !== null) {
      if (type === "percent") {
        return `${code} (-${amount}%)`;
      } else {
        return `${code} (-$${amount.toFixed(2)})`;
      }
    }

    return code;
  }

  /**
   * Get CSS class for coupon chip based on validity
   * @param coupon Coupon object
   * @returns CSS class string
   */
  getCouponClass(coupon: ScheduledOrderCoupon): string {
    return coupon.Valid ? "coupon-valid" : "coupon-invalid";
  }

  /**
   * Get icon for coupon chip based on validity
   * @param coupon Coupon object
   * @returns Icon class string
   */
  getCouponIcon(coupon: ScheduledOrderCoupon): string {
    return coupon.Valid ? "pi pi-check-circle" : "pi pi-times-circle";
  }

  /**
   * Get tooltip text for invalid coupons
   * Handles all 17 invalidation codes from old QMC
   * @param coupon Coupon object
   * @returns Tooltip text
   */
  getCouponTooltip(coupon: ScheduledOrderCoupon): string {
    if (coupon.Valid) {
      return "Valid coupon";
    }

    return this.getCouponInvalidationReason(coupon);
  }

  /**
   * Get user-friendly invalidation reason for a coupon
   * Matches old QMC implementation exactly (dashboard-scheduled-orders-create.component.ts lines 452-492)
   * Handles both string and number invalidation codes for API compatibility
   * @param coupon Coupon with invalidation code
   * @returns User-friendly error message
   */
  getCouponInvalidationReason(coupon: ScheduledOrderCoupon): string {
    // Get invalidation code from either location (coupon or nested Coupon object)
    const invalidationCode =
      coupon.CouponInvalidationCode || coupon.Coupon?.CouponInvalidationCode;

    if (!invalidationCode) {
      return "Invalid coupon";
    }

    // Handle both string codes (old API) and potential number codes (new API)
    // Convert to string for consistent comparison
    const codeStr = String(invalidationCode);

    // All 17 invalidation codes from old QMC
    switch (codeStr) {
      case "Expired":
        return "Coupon is expired";
      case "SiteLimitExceeded":
        return "Coupon reached its Site limit";
      case "ScheduledOrderLimitExceeded":
        return "Coupon reached its Scheduled Orders limit";
      case "CustomerLimitExceeded":
        return "Coupon reached its limit for this Customer";
      case "GeographyNotMet":
        return "Scheduled Order does not meet geography constraints";
      case "TotalsNotMet":
        return "Scheduled Order does not meet minimum Items subtotal amount";
      case "UnitsNotMet":
        return "Scheduled Order does not meet minimum number of Items to ship";
      case "MinWeightNotMet":
        return "Scheduled Order does not meet minimum weight of Items to ship";
      case "MaxWeightNotMet":
        return "Scheduled Order does not meet maximum weight of Items to ship";
      case "ScheduledOrderCyclesNotMet":
        return "Scheduled Order does not meet required number of cycles";
      case "CustomerInclusionList":
        return "Customer is not included in coupon inclusion list";
      case "CustomerExclusionList":
        return "Customer is excluded by coupon exclusion list";
      case "NonStackable":
        return "Coupon is not stackable";
      case "MaxAssignmentsReached":
        return "Coupon reached its assignment limit";
      case "ProductNotPresent":
        return "Target Product is not included in the Scheduled Order Items";
      case "SubtotalDiscountTypeMixed":
        return "Scheduled Order cannot include more than 1 coupon that changes the calculation of the Item Price";
      case "ItemHasSalePrice":
        return "Item has a sale price";
      default:
        return `Invalid coupon (code: ${codeStr})`;
    }
  }

  /**
   * Check if there are any coupons
   * @returns True if coupons exist
   */
  hasCoupons(): boolean {
    const couponsHistory = this.formGroup.controls.couponsHistory.value;
    return couponsHistory && couponsHistory.length > 0;
  }

  /**
   * Check if all coupons are valid
   * @returns True if all coupons are valid
   */
  allCouponsValid(): boolean {
    const couponsHistory = this.formGroup.controls.couponsHistory.value;
    return (
      couponsHistory &&
      couponsHistory.length > 0 &&
      couponsHistory.every((c) => c.Valid)
    );
  }

  /**
   * Check if any coupons are invalid
   * @returns True if any coupons are invalid
   */
  hasInvalidCoupons(): boolean {
    const couponsHistory = this.formGroup.controls.couponsHistory.value;
    return (
      couponsHistory &&
      couponsHistory.length > 0 &&
      couponsHistory.some((c) => !c.Valid)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
